// @oasis-permission-inherits: /creator-styles (ai-edit-v2-tool:view)
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  ExternalLink,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import { Textarea } from "~/components/ui/textarea";
import { useAuthStore } from "~/modules/auth/auth-store";
import {
  creatorStyleQuery,
  generateCreatorStylePreviewMutation,
  pollCreatorStyleQuery,
  refreshCreatorStyleSourceMutation,
  updateCreatorStyleMutation,
} from "~/modules/creator-styles/queries";
import {
  creatorStyleCategorySchema,
  creatorStyleCreatorTypeSchema,
  creatorStyleSortSchema,
  creatorStyleVisibilitySchema,
  type CreatorStyleCategory,
  type CreatorStyleCreatorType,
  type CreatorStyleResponse,
  type CreatorStyleVisibility,
} from "~/modules/creator-styles/schemas";
import { useResumableUpload } from "~/modules/file-upload/use-resumable-upload";

const listVisibilitySchema = z.union([
  creatorStyleVisibilitySchema,
  z.literal("active"),
  z.literal("all"),
]);

export const Route = createFileRoute("/_dash/creator-styles/$styleId")({
  validateSearch: z.object({
    q: z.string().optional().catch(undefined),
    visibility: listVisibilitySchema.default("active"),
    sort: creatorStyleSortSchema.default("rank"),
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { styleId } = Route.useParams();
  const search = Route.useSearch();
  const { data, error, isLoading } = creatorStyleQuery.useQuery({
    pathParams: { styleId },
  });
  const { begin: beginPolling, cancel: cancelPolling } = pollCreatorStyleQuery.usePollingQuery();

  useEffect(() => {
    if (data?.style.processingStatus !== "processing") {
      return;
    }
    beginPolling({ pathParams: { styleId } });
    return cancelPolling;
  }, [beginPolling, cancelPolling, data?.style.processingStatus, styleId]);

  return (
    <main className="space-y-6 pb-12">
      <Button variant="outline" size="sm" asChild>
        <Link to="/creator-styles" search={search}>
          <ArrowLeft className="mr-2 size-4" /> Creator Styles
        </Link>
      </Button>
      {isLoading && <p className="text-sm text-muted-foreground">Loading creator style...</p>}
      {error && <p className="text-sm text-destructive">{error.message}</p>}
      {data && <CreatorStyleEditor key={data.style.videoStyleId} data={data} />}
    </main>
  );
}

function CreatorStyleEditor({ data }: { data: CreatorStyleResponse }) {
  const { style } = data;
  const [user] = useAuthStore((state) => [state.user]);
  const canWrite = user?.permissions.includes("ai-edit-v2-tool:write") ?? false;
  const supportsLandingSlug = typeof style.reference.slug === "string";
  const [displayName, setDisplayName] = useState(style.displayName);
  const [slug, setSlug] = useState(style.reference.slug ?? "");
  const [creatorType, setCreatorType] = useState<CreatorStyleCreatorType>(style.creator.type);
  const [creatorDisplayName, setCreatorDisplayName] = useState(style.creator.displayName);
  const [creatorHandle, setCreatorHandle] = useState(style.creator.handle ?? "");
  const [creatorProfileUrl, setCreatorProfileUrl] = useState(style.creator.profileUrl ?? "");
  const [visibility, setVisibility] = useState<CreatorStyleVisibility>(
    style.availability.visibility
  );
  const [rank, setRank] = useState(String(style.rank));
  const [mirageStudio, setMirageStudio] = useState(style.availability.mirageStudio);
  const [iosEnabled, setIosEnabled] = useState(style.availability.ios.enabled);
  const [iosMinVersion, setIosMinVersion] = useState(style.availability.ios.minVersion ?? "");
  const [categories, setCategories] = useState<CreatorStyleCategory[]>(style.categories);
  const [autofillPrompt, setAutofillPrompt] = useState(style.autofillPrompt);
  const [marketingSummary, setMarketingSummary] = useState(style.marketingSummary);
  const [sampleDirectionPrompts, setSampleDirectionPrompts] = useState(() =>
    createSampleDirectionPromptFormItems(style.sampleDirectionPrompts)
  );
  const [hasContentEdits, setHasContentEdits] = useState(false);
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [sourceProject, setSourceProject] = useState(style.reference.sourceCaptionsProjectId);
  const [preserveCustomMedia, setPreserveCustomMedia] = useState(true);
  const [customPreviewVideoAsset, setCustomPreviewVideoAsset] = useState<string | null>(
    style.reference.customPreviewVideoAsset ?? null
  );
  const [customThumbnailPreviewAsset, setCustomThumbnailPreviewAsset] = useState<string | null>(
    style.reference.customThumbnailPreviewAsset ?? null
  );

  useEffect(() => {
    if (hasContentEdits) {
      return;
    }
    setAutofillPrompt(style.autofillPrompt);
    setMarketingSummary(style.marketingSummary);
    setSampleDirectionPrompts(createSampleDirectionPromptFormItems(style.sampleDirectionPrompts));
  }, [hasContentEdits, style.autofillPrompt, style.marketingSummary, style.sampleDirectionPrompts]);

  const updateStyle = updateCreatorStyleMutation.useMutation();
  const generatePreview = generateCreatorStylePreviewMutation.useMutation({
    onSuccess() {
      toast.success("Preview generation started");
    },
    onError(previewError) {
      toast.error(previewError instanceof Error ? previewError.message : "Preview failed");
    },
  });
  const refreshSource = refreshCreatorStyleSourceMutation.useMutation({
    onSuccess() {
      toast.success("Source update started");
    },
    onError(sourceError) {
      toast.error(sourceError instanceof Error ? sourceError.message : "Source update failed");
    },
  });
  const isSaving = updateStyle.isPending;
  const isMutating = isSaving || generatePreview.isPending || refreshSource.isPending;
  const isProcessing = style.processingStatus === "processing";
  const isEditingLocked = isMutating || isProcessing;
  const isFormDisabled = !canWrite || isEditingLocked;
  const hasCustomMedia = Boolean(
    style.reference.customPreviewVideoAsset || style.reference.customThumbnailPreviewAsset
  );
  const hasCustomMediaEdits =
    customPreviewVideoAsset !== (style.reference.customPreviewVideoAsset ?? null) ||
    customThumbnailPreviewAsset !== (style.reference.customThumbnailPreviewAsset ?? null);

  async function save() {
    const parsedRank = Number(rank);
    if (rank.trim() === "" || !Number.isSafeInteger(parsedRank)) {
      toast.error("Catalog rank must be a whole number");
      return;
    }
    const cleanedSampleDirectionPrompts = sampleDirectionPrompts.map(({ prompt, label }) => ({
      prompt: prompt.trim(),
      label: label.trim(),
    }));
    if (cleanedSampleDirectionPrompts.some(({ prompt, label }) => !prompt || !label)) {
      toast.error("Each sample direction needs a label and prompt");
      return;
    }
    try {
      await updateStyle.mutateAsync({
        pathParams: { styleId: style.videoStyleId },
        params: {
          displayName: displayName.trim(),
          ...(supportsLandingSlug ? { slug: slug.trim() } : {}),
          creatorType,
          ...(creatorType === "creator"
            ? {
                creatorDisplayName: creatorDisplayName.trim(),
                creatorHandle: creatorHandle.trim() || null,
                creatorProfileUrl: creatorProfileUrl.trim() || null,
              }
            : {}),
          visibility,
          rank: parsedRank,
          mirageStudio,
          iosEnabled,
          iosMinVersion: iosMinVersion.trim() || null,
          categories,
          autofillPrompt,
          marketingSummary,
          sampleDirectionPrompts: cleanedSampleDirectionPrompts,
        },
      });
      setHasContentEdits(false);
      toast.success("Creator style saved");
    } catch (saveError) {
      toast.error(saveError instanceof Error ? saveError.message : "Save failed");
    }
  }

  function openSourceDialog() {
    setSourceProject(style.reference.sourceCaptionsProjectId);
    setPreserveCustomMedia(true);
    setSourceDialogOpen(true);
  }

  function refreshProjectSource() {
    const source = sourceProject.trim();
    if (!source) {
      toast.error("Source Captions project is required");
      return;
    }
    void refreshSource
      .mutateAsync({
        pathParams: { styleId: style.videoStyleId },
        params: {
          source,
          preserveCustomMedia,
        },
      })
      .then((response) => {
        setCustomPreviewVideoAsset(response.style.reference.customPreviewVideoAsset ?? null);
        setCustomThumbnailPreviewAsset(
          response.style.reference.customThumbnailPreviewAsset ?? null
        );
        setSourceDialogOpen(false);
      })
      .catch(() => undefined);
  }

  function applyCustomMedia() {
    generatePreview.mutate({
      pathParams: { styleId: style.videoStyleId },
      params: {
        customPreviewVideoAsset,
        customThumbnailPreviewAsset,
      },
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">{style.displayName}</h1>
            <Badge variant="outline" className="capitalize">
              {style.processingStatus}
            </Badge>
          </div>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{style.videoStyleId}</p>
        </div>
        {canWrite && (
          <Button onClick={() => void save()} disabled={isEditingLocked}>
            {isSaving ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Save className="mr-2 size-4" />
            )}
            Save
          </Button>
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="space-y-6">
          <Section title="Catalog metadata">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Style name">
                <Input
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  disabled={isFormDisabled}
                />
              </Field>
              {supportsLandingSlug && (
                <Field label="Landing page slug">
                  <Input
                    value={slug}
                    onChange={(event) => setSlug(event.target.value)}
                    placeholder="style-name"
                    autoCapitalize="none"
                    spellCheck={false}
                    disabled={isFormDisabled}
                  />
                  <p className="text-xs text-muted-foreground">
                    {slug.trim()
                      ? `captions.ai/creators/${slug.trim()}`
                      : "Leave empty to keep this style off the marketing site."}
                  </p>
                </Field>
              )}
              <Field label="Catalog rank">
                <Input
                  type="number"
                  value={rank}
                  onChange={(event) => setRank(event.target.value)}
                  disabled={isFormDisabled}
                />
              </Field>
              <Field label="Creator type">
                <Select
                  value={creatorType}
                  onValueChange={(value) =>
                    setCreatorType(creatorStyleCreatorTypeSchema.parse(value))
                  }
                  disabled={isFormDisabled}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="creator">Creator</SelectItem>
                    <SelectItem value="captions">Captions</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {creatorType === "creator" && (
                <>
                  <Field label="Creator name">
                    <Input
                      value={creatorDisplayName}
                      onChange={(event) => setCreatorDisplayName(event.target.value)}
                      disabled={isFormDisabled}
                    />
                  </Field>
                  <Field label="Creator handle">
                    <Input
                      value={creatorHandle}
                      onChange={(event) => setCreatorHandle(event.target.value)}
                      disabled={isFormDisabled}
                    />
                  </Field>
                  <Field label="Creator profile URL">
                    <Input
                      value={creatorProfileUrl}
                      onChange={(event) => setCreatorProfileUrl(event.target.value)}
                      disabled={isFormDisabled}
                    />
                  </Field>
                </>
              )}
              <Field label="Visibility">
                <Select
                  value={visibility}
                  onValueChange={(value) =>
                    setVisibility(creatorStyleVisibilitySchema.parse(value))
                  }
                  disabled={isFormDisabled}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {creatorStyleVisibilitySchema.options.map((value) => (
                      <SelectItem key={value} value={value} className="capitalize">
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <CategorySelector
                value={categories}
                onChange={setCategories}
                disabled={isFormDisabled}
              />
            </div>
          </Section>

          <Section title="Product availability">
            <div className="space-y-4">
              <ToggleRow
                label="Mirage Studio"
                detail="Available on the web Studio style picker."
                checked={mirageStudio}
                onCheckedChange={setMirageStudio}
                disabled={isFormDisabled}
              />
              <ToggleRow
                label="iOS"
                detail="Keep this off until the iOS client supports creator styles."
                checked={iosEnabled}
                onCheckedChange={setIosEnabled}
                disabled={isFormDisabled}
              />
              <Field label="Minimum iOS version">
                <Input
                  value={iosMinVersion}
                  onChange={(event) => setIosMinVersion(event.target.value)}
                  placeholder="For a future iOS release"
                  disabled={isFormDisabled || !iosEnabled}
                />
              </Field>
            </div>
          </Section>

          <Section
            title="Extracted marketing content"
            action={
              autofillPrompt === style.autofillPrompt && style.autofillPrompt ? (
                <Button type="button" variant="outline" size="sm" asChild>
                  <a
                    href={getStudioCreatorStylePrefillUrl(style.videoStyleId)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="mr-2 size-4" /> Open autofill in Studio
                  </a>
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled
                  title="Save the autofill prompt before opening it in Studio"
                >
                  <ExternalLink className="mr-2 size-4" /> Open autofill in Studio
                </Button>
              )
            }
          >
            {style.processingError && (
              <p className="text-sm text-destructive">{style.processingError}</p>
            )}
            <div className="space-y-4">
              <Field label="Autofill prompt">
                <Textarea
                  className="min-h-32"
                  value={autofillPrompt}
                  onChange={(event) => {
                    setHasContentEdits(true);
                    setAutofillPrompt(event.target.value);
                  }}
                  disabled={isFormDisabled}
                />
              </Field>
              <Field label="Marketing summary">
                <Textarea
                  value={marketingSummary}
                  onChange={(event) => {
                    setHasContentEdits(true);
                    setMarketingSummary(event.target.value);
                  }}
                  disabled={isFormDisabled}
                />
              </Field>
            </div>
          </Section>

          <SampleDirectionPromptsEditor
            value={sampleDirectionPrompts}
            onChange={(nextPrompts) => {
              setHasContentEdits(true);
              setSampleDirectionPrompts(nextPrompts);
            }}
            disabled={isFormDisabled}
          />
        </div>

        <div className="space-y-6">
          <Section
            title="Style preview"
            action={
              canWrite ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isEditingLocked}
                  onClick={() =>
                    generatePreview.mutate({
                      pathParams: { styleId: style.videoStyleId },
                      params: {},
                    })
                  }
                >
                  {generatePreview.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
                  Generate preview
                </Button>
              ) : null
            }
          >
            <div className="overflow-hidden rounded-md bg-muted">
              <StylePreview
                previewVideoUrl={data.previewVideoUrl}
                thumbnailUrl={data.thumbnailUrl}
              />
            </div>
            {canWrite && (
              <div className="space-y-3 border-t pt-4">
                <div>
                  <div className="text-sm font-medium">Custom preview media</div>
                  <p className="text-xs text-muted-foreground">
                    Uploads stay private. Apply them to run the creator-style processing pipeline.
                  </p>
                </div>
                <CreatorStyleAssetUploader
                  type="video"
                  assetId={customPreviewVideoAsset}
                  onUploadComplete={setCustomPreviewVideoAsset}
                  onClear={() => setCustomPreviewVideoAsset(null)}
                  disabled={isEditingLocked}
                />
                <CreatorStyleAssetUploader
                  type="image"
                  assetId={customThumbnailPreviewAsset}
                  onUploadComplete={setCustomThumbnailPreviewAsset}
                  onClear={() => setCustomThumbnailPreviewAsset(null)}
                  disabled={isEditingLocked}
                />
                {hasCustomMediaEdits && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={applyCustomMedia}
                      disabled={isEditingLocked}
                    >
                      Apply preview media
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setCustomPreviewVideoAsset(style.reference.customPreviewVideoAsset ?? null);
                        setCustomThumbnailPreviewAsset(
                          style.reference.customThumbnailPreviewAsset ?? null
                        );
                      }}
                      disabled={isEditingLocked}
                    >
                      Discard
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Section>
          <Section
            title="Source and library copies"
            action={
              canWrite ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={openSourceDialog}
                  disabled={isEditingLocked}
                >
                  <RefreshCw className="mr-2 size-4" /> Update source
                </Button>
              ) : null
            }
          >
            <Definition
              label="Source Captions project"
              value={style.reference.sourceCaptionsProjectId}
            />
            <Definition
              label="Library Captions project"
              value={style.reference.libraryCaptionsProjectId}
            />
            <Definition
              label="Library Mirage project"
              value={style.reference.libraryMirageProjectId}
            />
            <Dialog open={sourceDialogOpen} onOpenChange={setSourceDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Update creator-style source?</DialogTitle>
                  <DialogDescription>
                    Use the same project ID to load its newest version. Enter another project ID or
                    Captions video URL to replace the source. The creator style ID will not change.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  {hasCustomMediaEdits && (
                    <div
                      role="alert"
                      className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm"
                    >
                      You have unapplied custom preview media changes. Updating the source will
                      discard those changes.
                    </div>
                  )}
                  <Field label="Source Captions project">
                    <Input
                      value={sourceProject}
                      onChange={(event) => setSourceProject(event.target.value)}
                      placeholder="Captions project ID or captions.ai/videos URL"
                      disabled={refreshSource.isPending}
                    />
                  </Field>
                  {hasCustomMedia && (
                    <div className="space-y-3">
                      <Label>Existing custom preview media</Label>
                      <div className="grid gap-2">
                        <Button
                          type="button"
                          variant={preserveCustomMedia ? "secondary" : "outline"}
                          className="h-auto justify-start whitespace-normal p-3 text-left"
                          onClick={() => setPreserveCustomMedia(true)}
                          disabled={refreshSource.isPending}
                          aria-pressed={preserveCustomMedia}
                        >
                          <span>
                            <span className="block text-sm font-medium">Keep custom media</span>
                            <span className="block text-xs text-muted-foreground">
                              Keep the custom video and thumbnail after the source update.
                            </span>
                          </span>
                        </Button>
                        <Button
                          type="button"
                          variant={!preserveCustomMedia ? "secondary" : "outline"}
                          className="h-auto justify-start whitespace-normal p-3 text-left"
                          onClick={() => setPreserveCustomMedia(false)}
                          disabled={refreshSource.isPending}
                          aria-pressed={!preserveCustomMedia}
                        >
                          <span>
                            <span className="block text-sm font-medium">
                              Replace with project media
                            </span>
                            <span className="block text-xs text-muted-foreground">
                              Generate the preview video and thumbnail from the new project version.
                            </span>
                          </span>
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSourceDialogOpen(false)}
                    disabled={refreshSource.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={refreshProjectSource}
                    disabled={!sourceProject.trim() || refreshSource.isPending}
                  >
                    {refreshSource.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
                    {hasCustomMediaEdits
                      ? "Discard changes and update source"
                      : "Update source and process"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </Section>
        </div>
      </div>
    </div>
  );
}

interface SampleDirectionPromptFormItem {
  id: string;
  prompt: string;
  label: string;
  rank: number | null;
}

function createSampleDirectionPromptFormItems(
  prompts: CreatorStyleResponse["style"]["sampleDirectionPrompts"]
): SampleDirectionPromptFormItem[] {
  return prompts.map(({ prompt, label, rank }, index) => ({
    id: `saved-${rank}-${index}`,
    prompt,
    label,
    rank,
  }));
}

function getStudioCreatorStylePrefillUrl(styleId: string) {
  const search = new URLSearchParams({
    stylePrefill: "creator",
    styleId,
  });
  return `https://captions.ai/home?${search}`;
}

function SampleDirectionPromptsEditor({
  value,
  onChange,
  disabled,
}: {
  value: SampleDirectionPromptFormItem[];
  onChange: (value: SampleDirectionPromptFormItem[]) => void;
  disabled: boolean;
}) {
  return (
    <Section
      title="Sample direction prompts"
      action={
        disabled ? null : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              onChange([
                ...value,
                {
                  id: crypto.randomUUID(),
                  prompt: "",
                  label: "",
                  rank: null,
                },
              ])
            }
          >
            <Plus className="mr-2 size-4" /> Add prompt
          </Button>
        )
      }
    >
      <p className="text-sm text-muted-foreground">
        Ways users can direct the style after it is applied. Labels appear as sections on the
        landing page.
      </p>
      {value.length === 0 ? (
        <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          No sample direction prompts yet.
        </div>
      ) : (
        <div className="space-y-3">
          {value.map((item, index) => (
            <div key={item.id} className="space-y-2 rounded-md border p-3">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor={`sample-direction-prompt-${item.id}`}>Direction {index + 1}</Label>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove sample direction ${index + 1}`}
                    onClick={() => onChange(value.filter(({ id }) => id !== item.id))}
                  >
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                )}
              </div>
              <Input
                value={item.label}
                onChange={(event) =>
                  onChange(
                    value.map((prompt) =>
                      prompt.id === item.id ? { ...prompt, label: event.target.value } : prompt
                    )
                  )
                }
                placeholder="Create B-roll"
                aria-label={`Label for sample direction ${index + 1}`}
                disabled={disabled}
              />
              <Textarea
                id={`sample-direction-prompt-${item.id}`}
                className="min-h-24"
                value={item.prompt}
                onChange={(event) =>
                  onChange(
                    value.map((prompt) =>
                      prompt.id === item.id ? { ...prompt, prompt: event.target.value } : prompt
                    )
                  )
                }
                placeholder="Describe one concrete way to direct this style"
                disabled={disabled}
              />
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function StylePreview({
  previewVideoUrl,
  thumbnailUrl,
}: {
  previewVideoUrl?: string | null;
  thumbnailUrl?: string | null;
}) {
  if (previewVideoUrl) {
    return (
      <video
        src={previewVideoUrl}
        controls
        playsInline
        className="w-full"
        poster={thumbnailUrl ?? undefined}
      />
    );
  }
  if (thumbnailUrl) {
    return <img src={thumbnailUrl} alt="" className="w-full" />;
  }
  return <div className="p-10 text-center text-sm text-muted-foreground">No preview asset</div>;
}

const CREATOR_STYLE_MEDIA_BUCKET = "ai-edit-artifacts";
const CREATOR_STYLE_MEDIA_PREFIX = "creator-styles/custom-inputs";
const MAX_CUSTOM_VIDEO_SIZE = 500 * 1024 * 1024;
const MAX_CUSTOM_IMAGE_SIZE = 10 * 1024 * 1024;

function CreatorStyleAssetUploader({
  type,
  assetId,
  onUploadComplete,
  onClear,
  disabled,
}: {
  type: "video" | "image";
  assetId: string | null;
  onUploadComplete: (assetId: string) => void;
  onClear: () => void;
  disabled: boolean;
}) {
  const isVideo = type === "video";
  const acceptedExtensions = isVideo
    ? [".mp4", ".mov", ".webm"]
    : [".jpg", ".jpeg", ".png", ".webp"];
  const acceptedMimeTypes = isVideo
    ? ["video/mp4", "video/quicktime", "video/webm"]
    : ["image/jpeg", "image/png", "image/webp"];
  const labels = isVideo
    ? {
        invalidTypeError: "Upload an MP4, MOV, or WebM video.",
        fileTooLargeError: "Preview videos must be 500 MB or smaller.",
        successMessage: "Preview video uploaded. Apply it to start processing.",
        errorMessage: "Preview video upload failed.",
      }
    : {
        invalidTypeError: "Upload a JPEG, PNG, or WebP image.",
        fileTooLargeError: "Thumbnail images must be 10 MB or smaller.",
        successMessage: "Thumbnail uploaded. Apply it to start processing.",
        errorMessage: "Thumbnail upload failed.",
      };
  const { isUploading, uploadProgress, fileInputRef, handleClick, handleFileInputChange } =
    useResumableUpload({
      acceptedExtensions,
      acceptedMimeTypes,
      maxFileSize: isVideo ? MAX_CUSTOM_VIDEO_SIZE : MAX_CUSTOM_IMAGE_SIZE,
      bucket: CREATOR_STYLE_MEDIA_BUCKET,
      prefix: CREATOR_STYLE_MEDIA_PREFIX,
      assetType: type,
      // The worker copies these staging inputs to the creator style's durable processed outputs.
      retention: "short_term",
      makePublic: false,
      getMimeType: (file) => file.type || "application/octet-stream",
      labels,
      onSuccess: onUploadComplete,
      disabled,
    });

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium">
            {isVideo ? "Custom preview video" : "Custom thumbnail"}
          </div>
          <div className="text-xs text-muted-foreground">
            {isVideo
              ? "Normalized and transcoded after apply."
              : "Converted to required JPEG sizes."}
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleClick}
          disabled={disabled || isUploading}
        >
          {isUploading ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <Upload className="mr-2 size-4" />
          )}
          {isUploading ? `${uploadProgress}%` : "Upload"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedMimeTypes.join(",")}
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>
      {assetId && (
        <div className="flex items-center gap-2 rounded bg-muted px-2 py-1.5">
          <code className="min-w-0 flex-1 truncate text-xs" title={assetId}>
            {assetId}
          </code>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={onClear}
            disabled={disabled || isUploading}
            aria-label={`Use project ${isVideo ? "video" : "thumbnail"}`}
            title={`Use project ${isVideo ? "video" : "thumbnail"}`}
          >
            <X className="size-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{title}</CardTitle>
        {action}
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="space-y-2">
      <Label>{label}</Label>
      {children}
    </label>
  );
}

function CategorySelector({
  value,
  onChange,
  disabled,
}: {
  value: CreatorStyleCategory[];
  onChange: (value: CreatorStyleCategory[]) => void;
  disabled: boolean;
}) {
  return (
    <div className="space-y-2 md:col-span-2">
      <Label>Categories</Label>
      <div className="flex flex-wrap gap-2">
        {creatorStyleCategorySchema.options.map((category) => {
          const selected = value.includes(category);
          return (
            <Button
              key={category}
              type="button"
              size="sm"
              variant={selected ? "secondary" : "outline"}
              aria-pressed={selected}
              disabled={disabled}
              onClick={() =>
                onChange(
                  selected ? value.filter((item) => item !== category) : [...value, category]
                )
              }
            >
              {category}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  detail,
  checked,
  onCheckedChange,
  disabled,
}: {
  label: string;
  detail: string;
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border p-3">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{detail}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </div>
  );
}

function Definition({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="break-all font-mono text-xs">{value}</div>
    </div>
  );
}
